import type { CollectionSchema } from '@next-space/fe-api-idl';
import { CollectionSchemaType } from '@next-space/fe-api-idl';
import { COLORS } from 'src/bitable/const';
import type { Option } from 'src/bitable/table-view/types';
import { segmentsToText, textToSegments } from 'src/editor/utils/editor';
import { getOwnerPage } from 'src/hooks/block/use-get-owner-page';
import { insertRecord } from 'src/hooks/block/use-insert-record';
import { getSortedRecordIds } from 'src/hooks/collection-view/use-get-sorted-records';
import { cache } from 'src/redux/store';
import { pickRandomValue } from 'src/utils/pick-random-value';
import { v4 as uuidV4 } from 'uuid';
import type { CellContent, PasteHook, RecordCells } from '../types';
import { getDynamicPageId } from 'src/utils/getPageId';
import { getAllExpandIds } from 'src/hooks/block/use-open-subitem';

export const parseContentToRecordCells = (content: string): RecordCells[] => {
  // 过滤掉双引号的换行
  const allLineContent = content.split(/(.*?(?:"[^"]*"*.*?)*)\r?\n/);
  const allRecordCells: RecordCells[] = [];
  let singleContent = false; // 如果格式不符合，这个值为true
  let lastCellLen = -1;
  for (const eachLineContent of allLineContent) {
    const recordCells: RecordCells = { cells: [] };
    const cells = eachLineContent.split('\t');
    if (cells.length === 1 && !cells[0]) {
      // 过滤掉空串
      continue;
    }

    if (lastCellLen === -1) {
      lastCellLen = cells.length;
    } else if (lastCellLen !== cells.length) {
      // 检查一下是不是每一行到cell个数都一致，不一致的话就说明格式不符合，只能以一个大的字符串对待
      singleContent = true;
      break;
    }
    if (cells.length === 0) {
      // 只有一个单元格的情况
      recordCells.cells.push(textToSegments(eachLineContent));
      continue;
    }
    cells.forEach((cell) => {
      recordCells.cells.push(textToSegments(cell));
    });
    allRecordCells.push(recordCells);
  }
  return singleContent ? [{ cells: [textToSegments(content)] }] : allRecordCells;
};

/** 粘贴单元格到单元格的处理 */
export const pasteCell = (
  { updatePropertyValueWithoutTransaction, createPropertyOption, transaction }: PasteHook,
  pasteContent: RecordCells[] | string // 有bug的话请找xiwei改，谢谢！
) => {
  let records: RecordCells[];
  if (Array.isArray(pasteContent)) {
    records = pasteContent;
  } else {
    records = parseContentToRecordCells(pasteContent);
  }
  if (!cache.ui.selectedCells.length) return; // 这里不应该发生但是我们还是检查一下
  const focusedCell = cache.ui.selectedCells[0];
  if (!focusedCell) return; // it's impossible
  const collectionBlockId = getOwnerPage(focusedCell.recordId);
  if (!collectionBlockId) return;
  const collectionBlock = cache.blocks[collectionBlockId];
  if (!collectionBlock) return;
  const { viewId, groupValue } = focusedCell;
  if (!viewId) return;
  const tryCreateNewOption = (
    schema: CollectionSchema,
    propertyId: string,
    cellValue: CellContent
  ) => {
    // 去掉逗号
    const text = segmentsToText(cellValue).replace(/,/g, '').trim();
    if (!text) return cellValue;
    const option = schema.options?.find((item) => item.value === text);
    // 如果已经有就不需要创建了
    if (option) {
      return textToSegments(text);
    }
    const pool = COLORS.map((item) => item.key);
    const color =
      pickRandomValue(pool, schema.options?.map((item) => item.color) ?? []) ?? COLORS[0].key;
    const newOption: Option = {
      id: uuidV4(),
      value: text,
      color,
    };
    createPropertyOption?.(collectionBlockId, propertyId, newOption, false);
    return textToSegments(text);
  };

  // 如果只粘贴一个文本，但选中了多个cell的情况，我们把文本填充到每一个cell上(notion/excel也是如此)
  if (records.length === 1 && records[0]?.cells.length === 1) {
    transaction(() => {
      cache.ui.selectedCells.forEach((cell) => {
        const collectionBlock = cache.blocks[collectionBlockId];
        if (!collectionBlock) return;
        const schema = collectionBlock.data.schema ?? {};
        const propertySchema = schema[cell.propertyId];
        if (!propertySchema) return;
        const cellsContent = records[0]?.cells.slice(0, 1);
        if (propertySchema.type === CollectionSchemaType.SELECT) {
          // 尝试创建一个新的单选
          if (!cellsContent) return;
          const cellContent = cellsContent[0];
          if (cellContent) {
            // 重新赋值（内容可能有逗号,去掉）
            cellsContent[0] = tryCreateNewOption(propertySchema, cell.propertyId, cellContent);
          }
        }
        // 多选
        if (propertySchema.type === CollectionSchemaType.MULTI_SELECT) {
          // 尝试创建一个新的option
          if (!cellsContent) return;
          const cellContent = cellsContent[0];
          if (cellContent) {
            segmentsToText(cellContent)
              .trim()
              .split(',')
              .forEach((t) => {
                // 拿最新的
                const collectionBlock = cache.blocks[collectionBlockId];
                if (!collectionBlock) return;
                const schema = collectionBlock.data.schema ?? {};
                const propertySchema = schema[cell.propertyId];
                if (!propertySchema) return;
                tryCreateNewOption(propertySchema, cell.propertyId, textToSegments(t));
              });
          }
        }
        updatePropertyValueWithoutTransaction?.(cell.recordId, cell.propertyId, cellsContent);
      });
    });
    return;
  }

  // 把cell内容放到剪切板，每条record占用一行，按左上到右下的顺序放置
  const propertyIds = cache.collectionViews[viewId]?.format.tableProperties
    ?.filter((property) => property.visible)
    .map((item) => item.property);

  const firstPasteRecord = records[0];
  if (!propertyIds || !firstPasteRecord) return;
  let { sortedRecordIds } = getSortedRecordIds({
    viewId,
    groupValue,
    pageId: getDynamicPageId(),
    openSubItem: true,
  });
  sortedRecordIds = getAllExpandIds(collectionBlockId, viewId, sortedRecordIds);
  const focusedRecordIndex = sortedRecordIds.indexOf(focusedCell.recordId);
  const focusedPropertyIndex = propertyIds.indexOf(focusedCell.propertyId);
  // 根据当前选中的单元格所对应的所有条目与粘贴过来的条目做对比，取一个合适的index,超出范围的会舍弃掉
  // const endRecordIndex = Math.min(records.length + focusedRecordIndex, sortedRecordIds.length);
  const endRecordIndex = records.length + focusedRecordIndex;
  const endPropertyIndex = Math.min(
    firstPasteRecord.cells.length + focusedPropertyIndex,
    propertyIds.length
  );
  let prevId: string | undefined;
  transaction(() => {
    // 一行一行更新
    for (let i = focusedRecordIndex; i < endRecordIndex; i++) {
      // 因为添加选项后需要拿到最新的schema，所以在for循环里每次都拿最新的collectionBlock
      const collectionBlock = cache.blocks[collectionBlockId];
      if (!collectionBlock) return;
      const schema = collectionBlock.data.schema ?? {};
      let recordId = sortedRecordIds[i];
      if (!recordId) {
        // 拿不到recordId说明没有，需要创建一条新的
        recordId = insertRecord(viewId, { after: prevId });
      }
      const newPropertyIds: string[] = [];
      const recordCells = records[i - focusedRecordIndex];
      if (!recordCells || !recordId) continue;
      // 截取用到的内容
      const cellsContent = recordCells.cells.slice(0, endPropertyIndex - focusedPropertyIndex);
      for (let j = focusedPropertyIndex; j < endPropertyIndex; j++) {
        const propertyId = propertyIds[j];
        if (!propertyId) return;
        const propertySchema = schema[propertyId];
        if (!propertySchema) return;
        newPropertyIds.push(propertyId);
        if (propertySchema.type === CollectionSchemaType.SELECT) {
          // 尝试创建一个新的单选或者多选选项
          const cellContent = cellsContent[j - focusedPropertyIndex];
          if (cellContent) {
            // 重新赋值（内容可能有逗号,去掉）
            cellsContent[j - focusedPropertyIndex] = tryCreateNewOption(
              propertySchema,
              propertyId,
              cellContent
            );
          }
        }
        // 多选
        if (propertySchema.type === CollectionSchemaType.MULTI_SELECT) {
          // 尝试创建一个新的option
          if (!cellsContent) return;
          const cellContent = cellsContent[j - focusedPropertyIndex];
          if (cellContent) {
            segmentsToText(cellContent)
              .trim()
              .split(',')
              .forEach((t) => {
                // 拿最新的
                const collectionBlock = cache.blocks[collectionBlockId];
                if (!collectionBlock) return;
                const schema = collectionBlock.data.schema ?? {};
                const propertyId = propertyIds[j];
                if (!propertyId) return;
                const propertySchema = schema[propertyId];
                if (!propertySchema) return;
                tryCreateNewOption(propertySchema, propertyId, textToSegments(t));
              });
          }
        }
      }
      updatePropertyValueWithoutTransaction?.(recordId, newPropertyIds, cellsContent);
      prevId = recordId;
    }
  });
};
